import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  Filter,
  Eye,
  UserPlus,
  Calendar,
  Pill,
  AlertCircle,
  AlertTriangle,
  Loader2,
  User,
  Trash2,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

// Import extracted components
import { OrderForm, ServiceAssignmentPanel, OrderDetailDialog, OrderDetailPanel } from '../components/orders';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusColors = {
  received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  validated: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  pending_assignment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  pending_insurer_confirmation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  confirmed_by_insurer: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  specialist_assigned: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  assigned: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  scheduled: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  in_progress: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  rejected: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400'
};

const priorityColors = {
  low: 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400',
  normal: 'border-blue-300 text-blue-600 dark:border-blue-600 dark:text-blue-400',
  high: 'border-orange-400 text-orange-600 dark:border-orange-500 dark:text-orange-400',
  urgent: 'border-red-500 text-red-600 dark:border-red-500 dark:text-red-400'
};

export const OrdersPage = () => {
  const { t, i18n } = useTranslation('orders');
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [services, setServices] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOneOpen, setIsDeleteOneOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/orders?${params.toString()}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients?status=active`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services?status=active`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, []);

  const fetchMedications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/stock`);
      setMedications(response.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
    fetchServices();
    fetchMedications();
  }, [fetchPatients, fetchServices, fetchMedications]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchAvailableSpecialists = async (city) => {
    try {
      const params = new URLSearchParams();
      if (city) params.append('city', city);
      
      const response = await axios.get(`${API_URL}/api/specialists/available?${params.toString()}`);
      setSpecialists(response.data);
    } catch (error) {
      console.error('Error fetching specialists:', error);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/orders`, data);
      toast.success(t('messages.created'));
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(errorMsg || t('messages.errorCreating'));
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || detail.message || JSON.stringify(detail));
      } else {
        toast.error(detail || t('messages.errorCreating'));
      }
      throw error;
    }
  };

  const handleAssign = async (orderId, assignment) => {
    try {
      await axios.post(`${API_URL}/api/orders/${orderId}/assign`, {
        service_item_id: assignment.service_item_id,
        specialist_id: assignment.specialist_id,
        scheduled_date: assignment.scheduled_date || null,
        scheduled_time: assignment.scheduled_time || null
      });
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && !Array.isArray(detail)) {
        toast.error(detail.msg || detail.message || t('messages.errorAssigning'));
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message).join(', ') || t('messages.errorAssigning'));
      } else {
        toast.error(detail || t('messages.errorAssigning'));
      }
      throw error;
    }
  };

  const handleDispenseMedications = async (orderId) => {
    try {
      const response = await axios.post(`${API_URL}/api/orders/${orderId}/dispense-medications`);
      toast.success(t('messages.dispensed'));
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          if (typeof err === 'string') {
            toast.error(err);
          } else {
            toast.error(err.msg || err.message || 'Error');
          }
        });
      }
      const orderResponse = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setSelectedOrder(orderResponse.data);
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || t('messages.errorDispensing'));
    }
  };

  const handleStatusChange = async (orderId, action, data = {}) => {
    try {
      let endpoint = `${API_URL}/api/orders/${orderId}/${action}`;
      let params = {};
      
      if (action === 'complete') {
        params = { notes: data.notes };
      } else if (action === 'reject') {
        params = { reason: data.reason };
      } else if (action === 'reassign') {
        params = {
          new_specialist_id: data.new_specialist_id,
          reason: data.reason,
          scheduled_date: data.scheduled_date,
          scheduled_time: data.scheduled_time
        };
      } else if (action === 'cancel') {
        params = { reason: data.reason };
      }
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await axios.post(endpoint, null, { params });
      toast.success(response.data.message);
      
      const orderResponse = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setSelectedOrder(orderResponse.data);
      fetchOrders();
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || `Error`);
      throw error;
    }
  };

  const openAssign = async (order) => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${order.id}`);
      setSelectedOrder(response.data);
      await fetchAvailableSpecialists(response.data.service_city || order.city);
      setIsAssignOpen(true);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    }
  };

  const openView = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setSelectedOrder(response.data);
      await fetchAvailableSpecialists(response.data.service_city);
      setIsViewOpen(true);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/api/orders`);
      toast.success(response.data.message || t('messages.allDeleted', 'All orders deleted successfully'));
      fetchOrders();
      setIsDeleteAllOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || t('messages.errorDeleting', 'Error deleting orders'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteOne = async () => {
    if (!orderToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/orders/${orderToDelete.id}`);
      toast.success(t('messages.orderDeleted', 'Order deleted successfully'));
      fetchOrders();
      setIsDeleteOneOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || t('messages.errorDeleting', 'Error deleting order'));
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (order) => {
    setOrderToDelete(order);
    setIsDeleteOneOpen(true);
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'orders_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.templateDownloaded') || 'Template downloaded');
    } catch (error) {
      toast.error(t('importExport.errorDownloadingTemplate') || 'Error downloading template');
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/orders/export/data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `orders_export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.exportSuccess', { count: orders.length }) || `${orders.length} orders exported`);
    } catch (error) {
      toast.error(t('importExport.errorExporting') || 'Error exporting orders');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error(t('importExport.onlyCsv') || 'Only CSV files are allowed');
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API_URL}/api/orders/import?skip_duplicates=true`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(t('importExport.importSuccess', { count: response.data.imported }) || `${response.data.imported} orders imported`);
        fetchOrders();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('importExport.errorImporting') || 'Error importing orders');
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
    return new Date(dateStr).toLocaleDateString(locale);
  };

  // Status labels from translations
  const statusOptions = [
    { key: 'received', label: t('status.received') },
    { key: 'processing', label: t('status.processing') },
    { key: 'validated', label: t('status.validated') },
    { key: 'pending_assignment', label: t('status.pending_assignment') },
    { key: 'assigned', label: t('status.assigned') },
    { key: 'in_progress', label: t('status.in_progress') },
    { key: 'completed', label: t('status.completed') },
    { key: 'cancelled', label: t('status.cancelled') },
    { key: 'rejected', label: t('status.rejected') }
  ];

  return (
    <div className="p-2 space-y-6" data-testid="orders-page" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--ink)]" style={{ letterSpacing: '-0.02em' }}>
            {t('title')}
          </h1>
          <p className="text-[var(--ink-3)] mt-1 text-sm">
            {t('subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={() => setIsImportOpen(true)}
            data-testid="import-orders-btn"
            className="border-[var(--line)] hover:bg-[var(--bg-soft)]"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('importExport.import') || 'Import'}
          </Button>
          <Button 
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || orders.length === 0}
            data-testid="export-orders-btn"
            className="border-[var(--line)] hover:bg-[var(--bg-soft)]"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {t('importExport.export') || 'Export'}
          </Button>
          {orders.length > 0 && (
            <Button 
              variant="destructive"
              onClick={() => setIsDeleteAllOpen(true)}
              data-testid="delete-all-orders-btn"
              className="bg-[var(--red)] hover:bg-[var(--red-deep)]"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('deleteAll', 'Delete All')}
            </Button>
          )}
          <Button 
            className="bg-[var(--red)] hover:bg-[var(--red-deep)] shadow-[var(--shadow-red)]"
            onClick={() => setIsFormOpen(true)}
            data-testid="add-order-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('newOrder')}
          </Button>
        </div>
      </div>

      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={isDeleteAllOpen} onOpenChange={setIsDeleteAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteAllTitle', 'Delete All Orders?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteAllDescription', `This will permanently delete all ${orders.length} orders from the system. This action cannot be undone.`).replace('${count}', orders.length)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common:cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAll}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('deleteAllConfirm', 'Yes, Delete All')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Single Order Confirmation Dialog */}
      <AlertDialog open={isDeleteOneOpen} onOpenChange={setIsDeleteOneOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteOneTitle', 'Delete Order?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteOneDescription', 'This will permanently delete the order for patient {{patient}}. This action cannot be undone.', { patient: orderToDelete?.patient_name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common:cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOne}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('deleteOneConfirm', 'Yes, Delete')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filters */}
      <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ink-3)]" />
              <Input
                placeholder={t('searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[var(--bg-soft)] border-[var(--line)] focus:border-[var(--red)]"
                data-testid="order-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[var(--bg-soft)] border-[var(--line)]" data-testid="order-status-filter">
                <Filter className="h-4 w-4 mr-2 text-[var(--ink-3)]" />
                <SelectValue placeholder={t('table.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:all', 'Todos')}</SelectItem>
                {statusOptions.map(({ key, label }) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-3 border-[var(--line)] border-t-[var(--red)] rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full" data-testid="orders-table">
                <thead>
                  <tr className="bg-[var(--bg-soft)]">
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.patient')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.services')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.medications')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.status')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.priority')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.specialists')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.date')}</th>
                    <th className="px-4 py-3 bg-muted text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                        {t('table.noOrders')}
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-foreground">{order.patient_name}</span>
                            {order.requires_manual_review && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-400"
                                title={
                                  order.manual_review_reasons?.length > 0
                                    ? order.manual_review_reasons.join('\n')
                                    : t('table.requiresManualReviewTooltip')
                                }
                                data-testid={`manual-review-badge-${order.id}`}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                {t('table.requiresManualReview')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{order.patient_document}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {order.services?.slice(0, 2).map((service, idx) => (
                              <Badge key={`${order.id}-service-${idx}`} variant="outline" className="text-xs">
                                {service}
                              </Badge>
                            ))}
                            {order.services?.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{order.services.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {order.medications_count > 0 ? (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800">
                              <Pill className="h-3 w-3 mr-1" />
                              {order.medications_count}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={statusColors[order.status]}>
                            {t(`status.${order.status}`, order.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={priorityColors[order.priority]}>
                            {t(`priority.${order.priority}`, order.priority)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {order.specialists && order.specialists.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {order.specialists.slice(0, 2).map((spec, idx) => (
                                <span key={`${order.id}-spec-${idx}`} className="flex items-center gap-1 text-xs">
                                  <User className="h-3 w-3 text-cyan-600 dark:text-cyan-400" />
                                  {spec}
                                </span>
                              ))}
                              {order.specialists.length > 2 && (
                                <span className="text-xs text-muted-foreground">{t('table.moreSpecialists', { count: order.specialists.length - 2 })}</span>
                              )}
                              {order.services_count > order.services_assigned && (
                                <span className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {order.services_count - order.services_assigned} {t('table.unassigned')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {t('table.unassigned')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {order.scheduled_date ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(order.scheduled_date)}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openView(order.id)}
                              data-testid={`view-order-${order.id}`}
                              title={t('actions.view')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {['received', 'pending_assignment', 'validated', 'assigned'].includes(order.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-primary"
                                onClick={() => openAssign(order)}
                                data-testid={`assign-order-${order.id}`}
                                title={t('actions.assign')}
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => openDeleteDialog(order)}
                              data-testid={`delete-order-${order.id}`}
                              title={t('actions.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('form.title')}</DialogTitle>
            <DialogDescription>
              {t('form.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <OrderForm
            patients={patients}
            services={services}
            medications={medications}
            onSubmit={handleCreate}
            onClose={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Assign Panel (Right Side Sheet) */}
      <ServiceAssignmentPanel
        order={selectedOrder}
        specialists={specialists}
        onAssign={handleAssign}
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
      />

      {/* View Order Panel (Right Side Sheet) */}
      <OrderDetailPanel
        order={selectedOrder}
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        specialists={specialists}
        onDispenseMedications={handleDispenseMedications}
        onStatusChange={handleStatusChange}
        onAssign={handleAssign}
        onRefresh={fetchOrders}
      />

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              {t('importExport.importTitle') || 'Import Orders'}
            </DialogTitle>
            <DialogDescription>
              {t('importExport.importDescription') || 'Upload a CSV file to import orders. Download the template first to see the required format.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Download */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('importExport.downloadTemplate') || 'Download Template'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('importExport.templateHint') || 'Get the CSV format with all required fields'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  {t('importExport.template') || 'Template'}
                </Button>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>{t('importExport.selectFile') || 'Select CSV File'}</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="flex-1"
                />
              </div>
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  {t('importExport.selectedFile') || 'Selected'}: {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-medium">{t('importExport.results') || 'Import Results'}</h4>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-green-600">{importResults.imported}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.imported') || 'Imported'}</p>
                  </div>
                  <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.skipped') || 'Skipped'}</p>
                  </div>
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-red-600">{importResults.errors?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.errors') || 'Errors'}</p>
                  </div>
                </div>

                {importResults.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-destructive mb-1">{t('importExport.errorDetails') || 'Error Details'}:</p>
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                      {importResults.errors.slice(0, 10).map((err, idx) => (
                        <p key={idx} className="text-muted-foreground">
                          Row {err.row}: {err.error}
                        </p>
                      ))}
                      {importResults.errors.length > 10 && (
                        <p className="text-muted-foreground">...and {importResults.errors.length - 10} more errors</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Format Info */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">{t('importExport.formatInfo') || 'CSV Format Information'}:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>{t('importExport.requiredFields') || 'Required: patient_document_number, service_code'}</li>
                <li>{t('importExport.patientMatch') || 'Patient must already exist in the system'}</li>
                <li>{t('importExport.serviceMatch') || 'Service code must match an active service'}</li>
                <li>{t('importExport.multiService') || 'Multiple services per order: use same patient + external_order_id'}</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog}>
              {t('common:cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importFile || isImporting}
              className="bg-primary hover:bg-primary/90"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('importExport.importing') || 'Importing...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('importExport.import') || 'Import'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
